import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { AuthProvider } from "./providers/AuthProvider";
import { DataModeProvider } from "./providers/DataModeProvider";
import { PipelinePage } from "./features/notes/PipelinePage";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { OverviewPage } from "./pages/OverviewPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      // Only retry server errors (5xx) and network failures (no status).
      // Non-5xx HTTP errors (4xx, 3xx) are deterministic — retrying won't help.
      // The `status` property is attached by api.ts's request() function.
      retry: (failureCount, error) => {
        const status = (error as Error & { status?: number }).status;
        if (status !== undefined && status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

// Code-split pages — each view loads independently
const PipelineFlowPage = lazy(() =>
  import("./pages/PipelineFlowPage").then((m) => ({
    default: m.PipelineFlowPage,
  })),
);
const SecurityPage = lazy(() =>
  import("./pages/SecurityPage").then((m) => ({ default: m.SecurityPage })),
);
const HealthPage = lazy(() =>
  import("./pages/HealthPage").then((m) => ({ default: m.HealthPage })),
);
const KnowledgePage = lazy(() =>
  import("./pages/KnowledgePage").then((m) => ({ default: m.KnowledgePage })),
);
const TracesPage = lazy(() =>
  import("./pages/TracesPage").then((m) => ({ default: m.TracesPage })),
);
const InfraPage = lazy(() =>
  import("./pages/InfraPage").then((m) => ({ default: m.InfraPage })),
);
const AuditPage = lazy(() =>
  import("./pages/AuditPage").then((m) => ({ default: m.AuditPage })),
);
const IntegrationsPage = lazy(() =>
  import("./pages/IntegrationsPage").then((m) => ({
    default: m.IntegrationsPage,
  })),
);

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-sm text-text-muted animate-pulse">Loading...</div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DataModeProvider>
          <BrowserRouter>
            <Routes>
              {/* Demo-only: the auth backend (Keycloak/API) is decommissioned.
                  Legacy auth routes redirect home so no dead sign-in/callback
                  UI is reachable and no auth network call can fire. */}
              <Route path="/callback" element={<Navigate to="/" replace />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route element={<DashboardLayout />}>
                <Route path="/" element={<OverviewPage />} />
                <Route
                  path="/pipeline"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PipelineFlowPage />
                    </Suspense>
                  }
                />
                <Route path="/pipeline/:id" element={<PipelinePage />} />
                <Route
                  path="/security"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SecurityPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/health"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <HealthPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/knowledge"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <KnowledgePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/traces"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <TracesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/infra"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <InfraPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/audit"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AuditPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/integrations"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <IntegrationsPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Routes>
          </BrowserRouter>
        </DataModeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
